import React from 'react';
import { Edit, FileText, DollarSign, Calendar, User, Tag, AlertCircle, MessageSquare, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { ContactOpportunity } from '@/hooks/useContactDetails';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface OpportunityCardProps {
  opportunity: ContactOpportunity;
  onEdit: (opportunity: ContactOpportunity) => void;
}

const statusConfig = {
  open: {
    label: 'Aberta',
    borderColor: 'border-blue-500/40',
    bgColor: 'bg-blue-500/5',
    badgeClass: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  },
  won: {
    label: 'Ganha',
    borderColor: 'border-green-500/40',
    bgColor: 'bg-green-500/5',
    badgeClass: 'bg-green-500/20 text-green-400 border-green-500/30',
  },
  lost: {
    label: 'Perdida',
    borderColor: 'border-red-500/40',
    bgColor: 'bg-red-500/5',
    badgeClass: 'bg-red-500/20 text-red-400 border-red-500/30',
  },
};

export const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity, onEdit }) => {
  const status = opportunity.status || 'open';
  const config = statusConfig[status];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return null;
    try {
      return format(new Date(date), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return null;
    }
  };

  return (
    <div
      className={`p-4 rounded-lg border ${config.borderColor} ${config.bgColor} hover:border-opacity-60 transition-all`}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Badge className={`${config.badgeClass} border text-xs px-2 py-0.5`}>
            {config.label}
          </Badge>
          <span className="text-sm font-medium text-neutral-50 truncate">
            {opportunity.title || 'Sem titulo'}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 hover:bg-neutral-600 flex-shrink-0"
          onClick={() => onEdit(opportunity)}
        >
          <Edit className="w-3.5 h-3.5 text-neutral-400" />
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <Layers className="w-4 h-4 text-neutral-500 flex-shrink-0" />
          <span className="text-neutral-400">
            {opportunity.pipeline_name} / {opportunity.stage_name}
          </span>
        </div>

        {opportunity.product && (
          <div className="flex items-center gap-2 text-sm">
            <FileText className="w-4 h-4 text-neutral-500 flex-shrink-0" />
            <span className="text-neutral-300">{opportunity.product}</span>
          </div>
        )}

        {(opportunity.price !== undefined && opportunity.price > 0) && (
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-neutral-500 flex-shrink-0" />
            <span className="text-neutral-300 font-medium">
              {formatCurrency(opportunity.price)}
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-neutral-500 flex-shrink-0" />
          <span className="text-neutral-400">
            Criado em {formatDate(opportunity.created_at) || 'Data desconhecida'}
          </span>
        </div>

        {status === 'won' && (
          <>
            {opportunity.sale_date && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="text-neutral-300">
                  Venda em {formatDate(opportunity.sale_date)}
                </span>
              </div>
            )}
            {opportunity.won_by_user_name && (
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="text-neutral-300">
                  Vendido por {opportunity.won_by_user_name}
                </span>
              </div>
            )}
          </>
        )}

        {status === 'lost' && (
          <>
            {opportunity.closed_at && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-neutral-300">
                  Perdida em {formatDate(opportunity.closed_at)}
                </span>
              </div>
            )}
            {opportunity.loss_reason_name && (
              <div className="flex items-center gap-2 text-sm">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-neutral-300">
                  Motivo: {opportunity.loss_reason_name}
                </span>
              </div>
            )}
          </>
        )}

        {opportunity.notes && (
          <div className="flex items-start gap-2 text-sm mt-2 pt-2 border-t border-neutral-700/50">
            <MessageSquare className="w-4 h-4 text-neutral-500 flex-shrink-0 mt-0.5" />
            <span className="text-neutral-400 line-clamp-2">{opportunity.notes}</span>
          </div>
        )}

        {opportunity.tags && opportunity.tags.length > 0 && (
          <div className="flex items-center gap-2 text-sm mt-2 pt-2 border-t border-neutral-700/50">
            <Tag className="w-4 h-4 text-neutral-500 flex-shrink-0" />
            <div className="flex flex-wrap gap-1">
              {opportunity.tags.map((tag, idx) => (
                <Badge key={idx} variant="outline" className="text-xs px-1.5 py-0 border-neutral-600 text-neutral-400">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpportunityCard;
